import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import DeleteIcon from '@material-ui/icons/Delete';
import InfoIcon from '@material-ui/icons/Info';
import SearchIcon from '@material-ui/icons/SearchOutlined';
import { pdf } from '@react-pdf/renderer';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';

import { FichePdfDocument } from '../components/FichePdfDocument';
import Message from '../components/Message';
import Pagination from '../components/Pagination';
import { Context } from '../components/Store';
import { getClients } from '../functions/client';
import { getFiche } from '../functions/fiche';
import {
  deleteFiche,
  deleteFiches,
  editStatus,
  getFiches,
} from '../functions/fiche';
import { getUsers } from '../functions/user';
import { getVehicles } from '../functions/vehicle';
import {
  mapStatus,
  mapSubtype,
  mapType,
  statusFicheList,
  subTypeFicheValues,
  typeBobinageValues,
  typeFicheList,
} from '../utils/constants';
import { formatDate, formatMillisToTime } from '../utils/utils';

export default function Home({ location }) {
  const [state] = useContext(Context);
  const [selectedFiches, setSelectedFiches] = useState([]);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [warning, setWarning] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [fiche, setFiche] = useState();
  const [fetchedData, setFetchedData] = useState(null);
  const [showPdf, setShowPdf] = useState(false);
  const isTechnicien = state.user?.status === 1;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    let fetchedVehicles = await getVehicles(state.token);
    let fetchedUsers = await getUsers(state.token, { status: [1, 2, 3] });
    let fetchedClients = await getClients(state.token, { limit: 5000 });
    setIsLoading(false);
    if (fetchedVehicles.error) return setError(fetchedVehicles.error);
    if (fetchedUsers.error) return setError(fetchedUsers.error);
    if (fetchedClients.error) return setError(fetchedClients.error);
    if (fetchedClients.data.length === 0)
      return setError("Veuillez d'abord créer un client");
    fetchedVehicles = [
      { value: '', label: 'Aucun véhicule sélectionné.' },
      ...fetchedVehicles.data.map((v) => {
        return { value: v._id, label: v.nom };
      }),
    ];
    fetchedUsers = [
      { value: '', label: 'Aucun responsable sélectionné.' },
      ...fetchedUsers.data.map((u) => {
        return {
          value: u._id,
          label: `${u.prenom || u.username} ${u.nom || ''}`,
        };
      }),
    ];
    fetchedClients = fetchedClients.data.map((c) => {
      return { value: c._id, label: c.enterprise };
    });
    return setFetchedData({
      clients: fetchedClients,
      users: fetchedUsers,
      vehicles: fetchedVehicles,
    });
  }, [state.token]);

  const fetchFiche = useCallback(async () => {
    setInfo('');
    setError('');
    setWarning('');
    setIsLoading(true);

    try {
      const fiches = await Promise.all(
        selectedFiches.map(async (id) => {
          const res = await getFiche(state.token, id);
          if (res.error) throw new Error(res.error);

          const data = res.data;

          return {
            ...data,
            ...(data.client && { client: data.client._id }),
            ...(data.resp && { resp: data.resp._id }),
            ...(data.author && { author: data.author.username }),
            ...(data.techniciens && { techniciens: data.techniciens }),
            ...(data.dateDebut && { dateDebut: formatDate(data.dateDebut) }),
            dureeTotale: formatMillisToTime(data?.dureeTotale || 0),
          };
        })
      );

      setFiche(fiches);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [state.token, selectedFiches]);

  useEffect(() => {
    fetchFiche();
    fetchData();
  }, [fetchFiche, fetchData]);

  /* Pagination */
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(1);
  const [fichesByPage, setFichesByPage] = useState(
    localStorage.getItem('fichesByPage') || 10
  );
  /* Year */
  const [yearFilter, setYearFilter] = useState(() => {
    const savedYear = localStorage.getItem('yearFilter');
    return savedYear ? JSON.parse(savedYear) : null;
  });

  const updateYearFilter = (newYear) => {
    setYearFilter(newYear);
    localStorage.setItem('yearFilter', JSON.stringify(newYear));
  };

  const handleChangeYearFilter = (selectedOption) => {
    updateYearFilter(selectedOption);
  };

  const animatedComponents = makeAnimated();

  const currentYear = new Date().getFullYear();
  const startYear = 2021;

  const yearOptions = Array.from(
    { length: currentYear - startYear + 1 },
    (_, i) => {
      const year = startYear + i;
      return { value: year, label: String(year) };
    }
  );

  /* Search */
  useEffect(() => {
    fetchFiches();
    if (localStorage.getItem('message')) {
      setInfo(localStorage.getItem('message'));
      localStorage.removeItem('message');
    }
    if (!query.has('status')) {
      history.replace({
        search: `${query.toString()}&${new URLSearchParams({
          status: [1, 2, 3, 4],
        })}`,
      });
    }
  }, []);

  const removeFiche = async (id, numFiche) => {
    setError('');
    setInfo('');
    if (
      window.confirm(
        `Confirmez la suppression de la fiche ${numFiche}. Cette action est définitive.`
      )
    ) {
      setIsLoading(true);
      const deleted = await deleteFiche(id, state.token);
      setIsLoading(false);
      if (deleted.error) return setError(deleted.error);
      fetchFiches();
      setWarning('');
      setError('');
      setInfo(`La fiche ${numFiche} a bien été supprimée.`);
    }
  };

  const selectFiche = (id) => {
    if (isTechnicien) return;
    if (!selectedFiches.includes(id)) {
      setSelectedFiches((selectedFiches) => [...selectedFiches, id]);
    } else {
      setSelectedFiches((selectedFiches) =>
        selectedFiches.filter((fiche) => fiche !== id)
      );
    }
  };

  const validateBtn = async () => {
    setInfo('');
    setError('');
    setWarning('');
    const action = document.getElementById('select-fiches').value;
    let res;
    switch (action) {
      case '1':
        res = await editStatus(selectedFiches, 4, state.token);
        if (res.some((r) => r.error))
          return setError(res.find((r) => r.error).error);
        setSelectedFiches([]);
        fetchFiches();
        setInfo(
          'Les fiches selectionnées ont été archivées. Vous pouvez les retrouver via les filtres de recherche.'
        );
        break;
      case '2':
        if (
          window.confirm(
            `Vous êtes sur le point de supprimer une ou plusieurs fiches. Confirmez la suppression (cette action est définitive) ?`
          )
        ) {
          setIsLoading(true);
          const res = await deleteFiches(selectedFiches, state.token);
          setIsLoading(false);
          if (res.some((r) => r.error))
            return setError(res.find((r) => r.error).error);
          setSelectedFiches([]);
          fetchFiches();
          setInfo(`Les fiches ont bien été supprimées.`);
        }
        break;
      case '3':
        if (!fiche || fiche.length === 0) {
          setError('Aucune fiche sélectionnée pour la génération du PDF.');
          return;
        }

        try {
          for (const f of fiche) {
            const singleFicheDoc = (
              <FichePdfDocument
                fiche={[f]}
                fetchedData={fetchedData}
                token={state.token}
              />
            );

            const blob = await pdf(singleFicheDoc).toBlob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            const num = f.numFiche || 'inconnue';
            a.href = url;
            a.download = `${num}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
          }
        } catch (e) {
          setError('Erreur lors de la génération des PDFs.');
          console.error(e);
        }
        break;
      default:
    }
  };
  const history = useHistory();
  const query = new URLSearchParams(location.search);
  const [text, setText] = useState(() => {
    const savedText = localStorage.getItem('text');
    if (savedText) {
      return savedText;
    } else {
      return '';
    }
  });
  const updateTextFilter = (text) => {
    //console.log(text);
    setText(text);
    localStorage.setItem('text', text);
  };
  const handleChangeTextFilter = (newTextFilter) => {
    updateTextFilter(newTextFilter.target.value);
  };
  const [typeFilter, setTypeFilter] = useState(() => {
    const savedTypeFilter = localStorage.getItem('typeFilter');
    if (savedTypeFilter) {
      return JSON.parse(savedTypeFilter);
    } else {
      return [];
    }
  });
  const updateTypeFilter = (newTypeFilter) => {
    setTypeFilter(newTypeFilter);
    const type = JSON.stringify(newTypeFilter);
    if (!type.includes('Démontage') && !type.includes('Remontage')) {
      updateSubtypeFilter([]);
    }
    if (!type.includes('Bobinage')) {
      updateTypeBobinageFilter([]);
    }
    localStorage.setItem('typeFilter', type);
  };
  const handleChangetypeFilter = (newTypeFilter) => {
    updateTypeFilter(newTypeFilter);
  };
  const [subTypeFilter, setSubTypeFilter] = useState(() => {
    const savedSubtypeFilter = localStorage.getItem('subTypeFilter');
    if (savedSubtypeFilter) {
      return JSON.parse(savedSubtypeFilter);
    } else {
      return [];
    }
  });
  const updateSubtypeFilter = (newSubtypeFilter) => {
    setSubTypeFilter(newSubtypeFilter);
    localStorage.setItem('subTypeFilter', JSON.stringify(newSubtypeFilter));
  };
  const handleChangeSubtypeFilter = (newSubtypeFilter) => {
    updateSubtypeFilter(newSubtypeFilter);
  };

  const [typeBobinageFilter, setTypeBobinageFilter] = useState(() => {
    const savedTypeBobinageFilter = localStorage.getItem('typeBobinageFilter');
    if (savedTypeBobinageFilter) {
      return JSON.parse(savedTypeBobinageFilter);
    } else {
      return [];
    }
  });

  const updateTypeBobinageFilter = (newTypeBobinageFilter) => {
    setTypeBobinageFilter(newTypeBobinageFilter);
    localStorage.setItem(
      'typeBobinageFilter',
      JSON.stringify(newTypeBobinageFilter)
    );
  };

  const handleChangeTypeBobinageFilter = (newTypeBobinageFilter) => {
    updateTypeBobinageFilter(newTypeBobinageFilter);
  };

  const [statusFilter, setStatusFilter] = useState(() => {
    const savedStatusFilter = localStorage.getItem('statusFilter');
    if (savedStatusFilter) {
      return JSON.parse(savedStatusFilter);
    } else {
      return statusFicheList.filter((s) => s.value < 4);
    }
  });

  const updateStatusFilter = (newStatusFilter) => {
    setStatusFilter(newStatusFilter);
    localStorage.setItem('statusFilter', JSON.stringify(newStatusFilter));
  };
  const handleChangeStatusFilter = (newStatusFilter) => {
    updateStatusFilter(newStatusFilter);
  };

  const [fiches, setFiches] = useState([]);

  const fetchFiches = async (options = {}) => {
    setInfo('');
    setError('');
    setWarning('');
    setIsLoading(true);
    const fetchedFiches = await getFiches(state.token, {
      limit: options.limit || fichesByPage,
      offset: options.offset || 0,
      filter: text.length > 3 ? text : options.filter,
      types: typeFilter?.map((t) => t?.value),
      subtypes: subTypeFilter?.map((t) => t?.value),
      status: statusFilter?.map((s) => s?.value),
      year: yearFilter?.value,
      typeBobinage: typeBobinageFilter?.map((t) => t?.value),
    });
    setIsLoading(false);
    if (fetchedFiches.error) return setError(fetchedFiches.error);
    setTotalCount(fetchedFiches.totalCount);

    // Filtrer les fiches pour les techniciens
    let filteredFiches = fetchedFiches.data || [];
    if (isTechnicien) {
      filteredFiches = filteredFiches.filter(
        (fiche) =>
          fiche.techniciens?.some((t) => t._id === state.user._id) ||
          fiche.resp?._id === state.user._id
      );
    }

    setFiches(filteredFiches);
  };

  useEffect(() => {
    fetchFiches();
    if (localStorage.getItem('message')) {
      setInfo(localStorage.getItem('message'));
      localStorage.removeItem('message');
    }
    if (!query.has('status')) {
      history.replace({
        search: `${query.toString()}&${new URLSearchParams({
          status: [1, 2, 3, 4],
        })}`,
      });
    }
  }, [
    text,
    typeFilter,
    subTypeFilter,
    statusFilter,
    yearFilter,
    typeBobinageFilter,
  ]); // eslint-disable-line

  const searchFiches = () => {
    fetchFiches();
  };

  const allSelected =
    selectedFiches.length === fiches.length && fiches.length > 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedFiches([]);
    } else {
      setSelectedFiches(fiches.map((fiche) => fiche._id));
    }
  };

  function CustomPagination() {
    return (
      <Pagination
        page={page}
        setPage={setPage}
        fetchFunction={fetchFiches}
        nbByPage={fichesByPage}
        setNbByPage={(nb) => {
          fetchFiches({ limit: nb });
          localStorage.setItem('fichesByPage', nb);
          return setFichesByPage(nb);
        }}
        totalCount={totalCount}
      />
    );
  }

  document.title = 'Accueil';

  return (
    <div className="flex flex-col items-center">
      <div className="w-full lg:w-11/12 mb-10">
        <h1 className="mt-6 text-2xl sm:text-3xl md:text-4xl uppercase font-bold mb-8">
          Accueil
        </h1>
        {error ? <Message message={error} setError={setError} /> : null}
        {warning ? (
          <Message message={warning} type="warning" setError={setWarning} />
        ) : null}
        {info ? (
          <Message message={info} type="success" setError={setInfo} />
        ) : null}
        {showPdf && (
          <GeneratePdf
            fiche={fiche}
            fetchedData={fetchedData}
            setError={setError}
          />
        )}
        <div className="bg-viguierBleu rounded">
          <div className="flex items-center justify-between py-2 font-bold bg-viguierBleu text-white rounded-t">
            <div className="w-1/3" />
            <div className="flex w-1/3 justify-center">
              <h2 className="mr-24">Fiches d'intervention</h2>
            </div>
            <div className="flex w-1/3 justify-end">
              {!isTechnicien && (
                <Link
                  to="/fiches/new"
                  className="mr-4 border py-1 px-2 rounded border-white hover:bg-white hover:text-viguierBleu"
                >
                  Nouvelle intervention
                </Link>
              )}
            </div>
          </div>
          <div className="bg-viguierBleu text-white justify-between flex px-2 py-2">
            <div className="flex ml-2 items-center flex-wrap">
              <form
                className="mr-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  searchFiches();
                }}
              >
                <input
                  type="search"
                  id="searchText"
                  value={text}
                  onChange={(e) => handleChangeTextFilter(e)}
                  placeholder="Rechercher une fiche d'intervention..."
                  title="Filtrer par numéro Batigest, client ou technicien"
                  className="w-full max-w-sm rounded pl-2 py-2 focus:outline-none text-black"
                />
              </form>
              <Select
                isMulti
                options={typeFicheList}
                value={typeFilter}
                onChange={handleChangetypeFilter}
                placeholder="Filtrer par type de fiche..."
                noOptionsMessage={() => ''}
                className="rounded mr-2 my-1 text-black"
                styles={{
                  control: (base) => ({
                    ...base,
                    border: 1,
                    boxShadow: 'none',
                  }),
                }}
              />
              {(() => {
                const hasDemontageOrRemontage = typeFilter.some((it) => {
                  const value = Number(it.value);
                  return value === 2 || value === 3;
                });
                const hasBobinage = typeFilter.some((it) => {
                  const value = Number(it.value);
                  return value === 4;
                });

                return (
                  <>
                    {hasDemontageOrRemontage && (
                      <Select
                        isMulti
                        options={subTypeFicheValues}
                        value={subTypeFilter}
                        onChange={handleChangeSubtypeFilter}
                        placeholder="Sélectionnez un type de moteur"
                        noOptionsMessage={() => ''}
                        className="rounded mr-2 my-1 text-black"
                        styles={{
                          control: (base) => ({
                            ...base,
                            border: 1,
                            boxShadow: 'none',
                          }),
                        }}
                      />
                    )}
                    {hasBobinage && (
                      <Select
                        isMulti
                        options={typeBobinageValues}
                        value={typeBobinageFilter}
                        onChange={handleChangeTypeBobinageFilter}
                        placeholder="Sélectionnez un type de bobinage"
                        noOptionsMessage={() => ''}
                        className="rounded mr-2 my-1 text-black"
                        styles={{
                          control: (base) => ({
                            ...base,
                            border: 1,
                            boxShadow: 'none',
                          }),
                        }}
                      />
                    )}
                  </>
                );
              })()}
              <span className="flex items-center flex-nowrap">
                <Select
                  isMulti
                  options={statusFicheList}
                  value={statusFilter}
                  onChange={handleChangeStatusFilter}
                  placeholder="Filtrer par statut..."
                  noOptionsMessage={() => ''}
                  className="rounded pr-2 text-black"
                  styles={{
                    control: (base) => ({
                      ...base,
                      border: 1,
                      boxShadow: 'none',
                    }),
                  }}
                />
                <div className="my-4">
                  <Select
                    id="year-select"
                    options={yearOptions}
                    components={animatedComponents}
                    value={yearFilter}
                    onChange={handleChangeYearFilter}
                    isClearable
                    placeholder="Filtrer par année"
                    noOptionsMessage={() => ''}
                    className="rounded pr-2 text-black"
                    styles={{
                      control: (base) => ({
                        ...base,
                        border: 1,
                        boxShadow: 'none',
                      }),
                    }}
                  />
                </div>
                <button
                  className="mr-4 ml-4 border py-1 px-2 rounded border-white hover:bg-white hover:text-viguierBleu "
                  onClick={(e) => {
                    e.stopPropagation();
                    updateTextFilter('');
                    updateStatusFilter([]);
                    updateTypeFilter([]);
                    updateSubtypeFilter([]);
                    updateYearFilter([]);
                    updateTypeBobinageFilter([]);
                  }}
                >
                  Suppression des filtres
                </button>
              </span>
            </div>
            <CustomPagination />
          </div>
          {totalCount > 0 && (
            <div className="mt-2 text-white font-semibold text-center">
              {fiches.length} {fiches.length === 1 ? 'fiche' : 'fiches'} sur{' '}
              {totalCount}
            </div>
          )}
          <div className="border-4 border-white bg-viguierBleuClair mt-5 mb-5 ml-5 mr-5 rounded-md hidden lg:block">
            <table className="w-11/12 mb-4 ml-16 bg-viguierGrisClair">
              <thead className="bg-viguierBleuClair text-white h-10">
                <tr>
                  <th
                    className="px-2 cursor-pointer"
                    onClick={!isTechnicien ? toggleSelectAll : undefined}
                  >
                    {!isTechnicien ? (
                      allSelected ? (
                        <CheckBoxIcon fontSize="medium" />
                      ) : (
                        <CheckBoxOutlineBlankIcon fontSize="medium" />
                      )
                    ) : (
                      <span>&nbsp;</span>
                    )}
                  </th>
                  <th>N° d'intervention</th> {/* Supprimez cette ligne */}
                  <th>Client</th>
                  <th>Type de réparation</th>
                  <th>Technicien responsable</th>
                  <th>Date de prise en charge</th>
                  <th>Statut</th>
                  <th className="px-1">Options</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="9">
                      <Message message="Chargement..." type="success" />
                    </td>
                  </tr>
                ) : fiches.length > 0 ? (
                  fiches.map((fiche, i) => {
                    const id = fiche._id;
                    return (
                      <tr
                        key={id}
                        onClick={() => {
                          if (!isTechnicien) {
                            selectFiche(id);
                          }
                          history.push(
                            `/fiches/${fiche._id}?${query.toString()}`
                          );
                        }}
                        className={`hover:bg-viguierBleu cursor-pointer hover:text-white ${
                          i % 2 === 0 && 'bg-viguierGrisFoncé'
                        }
                                        ${
                                          selectedFiches.includes(id) &&
                                          'bg-viguierBleu text-white'
                                        }`}
                      >
                        <td>
                          {selectedFiches.includes(id) ? (
                            <CheckBoxIcon
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isTechnicien) selectFiche(id);
                              }}
                            />
                          ) : (
                            <CheckBoxOutlineBlankIcon
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isTechnicien) selectFiche(id);
                              }}
                            />
                          )}
                        </td>

                        <td>{fiche.numFiche}</td>
                        <td>
                          {fiche.contact} - {fiche.client?.enterprise}
                        </td>
                        <td>{mapType(fiche.type)}</td>
                        <td>
                          {fiche.resp ? (
                            fiche.resp.prenom && fiche.resp.nom ? (
                              `${fiche.resp.prenom} ${fiche.resp.nom}`
                            ) : (
                              fiche.resp.username
                            )
                          ) : (
                            <i>Non assigné</i>
                          )}
                        </td>
                        <td>
                          {fiche.dateDebut ? (
                            formatDate(new Date(fiche.dateDebut), true)
                          ) : (
                            <i>Non renseignée</i>
                          )}
                        </td>
                        <td>{mapStatus(fiche.status)}</td>
                        <td>
                          {!isTechnicien && (
                            <button
                              className="hover:text-viguierBleuClair"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeFiche(fiche._id, fiche.numFiche);
                              }}
                            >
                              <DeleteIcon />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9">
                      <Message
                        message={"Aucune fiche n'a été trouvée"}
                        type="warning"
                        setError={(_) => {
                          setWarning('');
                          setText('');
                          fetchFiches({ filter: '' });
                        }}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="bg-viguierBleu text-white justify-between flex rounded-b px-2 py-2">
            <div>
              <div
                className={`${
                  selectedFiches.length === 0 || isTechnicien
                    ? 'hidden'
                    : 'flex items-center'
                }`}
              >
                <label htmlFor="select-fiches" className="mr-4">
                  Action sur la sélection :
                </label>
                <select
                  name="selectable"
                  id="select-fiches"
                  className="text-black rounded"
                >
                  <option value="1">Archiver</option>
                  <option value="2">Supprimer</option>
                  <option value="3">Télécharger</option>
                </select>
                <CheckCircleOutlineIcon
                  className="ml-3 hover:text-stbSecond cursor-pointer"
                  onClick={validateBtn}
                />
              </div>
            </div>
            <CustomPagination />
          </div>
        </div>
      </div>
      <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
        {fiches.map((fiche) => (
          <div
            key={fiche._id}
            className="bg-white shadow-lg rounded-lg overflow-hidden"
            onClick={() =>
              history.push(`/fiches/${fiche._id}?${query.toString()}`)
            }
          >
            <div className="px-4 py-2 bg-viguierBleuClair text-white font-semibold uppercase">
              {fiche.numFiche}
            </div>
            <div className="py-2 px-4">
              <h1 className="text-2xl font-semibold text-gray-800">
                {fiche.contact}
              </h1>
              <p className="mt-2 text-gray-600 text-sm">
                {mapType(fiche.type)}
              </p>
              <p className="mt-2 text-gray-600 text-sm">
                Technicien responsable:{' '}
                <div>
                  {fiche.resp
                    ? fiche.resp.prenom && fiche.resp.nom
                      ? `${fiche.resp.prenom} ${fiche.resp.nom}`
                      : fiche.resp.username
                    : 'Non assigné'}
                </div>
              </p>
              <p className="mt-2 text-gray-600 text-sm">
                Date :{' '}
                {fiche.dateDebut
                  ? formatDate(new Date(fiche.dateDebut), true)
                  : 'Non renseignée'}
              </p>
              <p className="mt-2 text-gray-600 text-sm">
                Statut:{' '}
                <span className="font-bold ml-2">
                  {mapStatus(fiche.status)}
                </span>
              </p>
            </div>
            <div className="py-2 px-4 flex justify-end">
              <button
                className="text-white bg-viguierBleu hover:bg-viguierBleuFoncé font-semibold py-2 px-4 rounded-full"
                onClick={() =>
                  history.push(`/fiches/${fiche._id}?${query.toString()}`)
                }
              >
                Voir la fiche
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
